import React, { useRef, useEffect } from 'react';
import { useMessageHandler } from '../contexts/MessageContext';
import { useTheme } from '@mui/material/styles';
import UserMessage from './UserMessage';
import AssistantMessage from './AssistantMessage';
import { useChatSession } from '../contexts/ChatSessionContext';
import { useUserSettings } from '../contexts/UserSettingsContext';
import { getWallpaperStyle } from '../constants/wallpaperConstants';

const ChatMessages = ({ fontSize, fontFamily, selectedEngine, selectedService, translit, autoOptimizePrompt, enableThinking }) => {
  const theme = useTheme();
  const { messages, currentSession } = useMessageHandler();
  const { projects } = useChatSession();
  const { settings } = useUserSettings();
  const chatContainerRef = useRef(null);

  // Find the project that contains the current session
  const currentProject = projects?.find(project =>
    project.sessions?.includes(currentSession)
  );
  
  // Get the font color from the project
  const projectFontColor = currentProject?.fontColor || 'default';

  // Get wallpaper styles if a wallpaper is selected, with the selected scaling option
  const wallpaperStyles = getWallpaperStyle(settings.wallpaper, settings.customWallpaper, settings.wallpaperScaling);

  // Combine base styles with wallpaper styles
  const chatContainerStyle = {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    overflowY: 'auto',
    flexGrow: 1,
    padding: '15px',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    ...wallpaperStyles
  };

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages]);

  const base64ToFile = (base64String, filename, mimeType) => {
    try {
      // Check if base64String already includes data URI prefix
      const base64WithoutPrefix = base64String.includes('base64,')
        ? base64String.split('base64,')[1]
        : base64String;

      const binaryString = window.atob(base64WithoutPrefix);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);

      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const blob = new Blob([bytes], { type: mimeType });
      return new File([blob], filename, { type: mimeType });
    } catch (error) {
      console.error('Error converting base64 to file:', error);
      return null;
    }
  };

  const processMessageImages = (message) => {
    if (message.images) {
      if (message.images.some(img => img.preview)) {
        return message.images;
      }
      return message.images.map(img => {
        const file = base64ToFile(img.base64, img.originalName, img.mimetype);
        return {
          ...img,
          preview: img.base64.includes('data:') ? img.base64 : `data:${img.mimetype};base64,${img.base64}`,
          file: file
        };
      }).filter(img => img.file !== null); // Filter out any failed conversions
    }
    return [];
  };

  const processMessageTextAttachments = (message) => {
    if (message.textAttachments) {
      // Convert any string textAttachments to array
      const attachments = Array.isArray(message.textAttachments)
        ? message.textAttachments
        : [message.textAttachments];

      return attachments.map(attachment => ({
        ...attachment,
        originalName: attachment.originalName || 'Document',
        content: attachment.content || '',
        type: attachment.type || 'text/plain'
      }));
    }
    return [];
  };

  const processMessagePdfAttachments = (message) => {
    if (message.pdfAttachments) {
      return message.pdfAttachments.map(pdf => ({
        ...pdf,
        originalName: pdf.originalName || 'Document.pdf',
        type: 'application/pdf',
        preview: pdf.base64,
      }));
    }
    return [];
  };

  return (
    <div style={chatContainerStyle} ref={chatContainerRef}>
      {/* Add a wallpaper overlay to control content visibility */}
      {(settings.wallpaper && settings.wallpaper !== 'default') && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          pointerEvents: 'none', // Allow clicking through this overlay
          zIndex: 0
        }} />
      )}
      {messages.map((message, index) => (
        <div
          key={`${message.role}-${index}`}
          style={{
            display: 'flex',
            justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
            position: 'relative',
            zIndex: 1 // Ensure messages are above the wallpaper
          }}>

          {message.role === 'user' ? (
            <UserMessage
              content={message.content}
              index={index}
              fontSize={fontSize}
              fontFamily={fontFamily}
              fontColor={projectFontColor}
              images={processMessageImages(message)}
              textAttachments={processMessageTextAttachments(message)}
              pdfAttachments={processMessagePdfAttachments(message)}
              selectedEngine={selectedEngine}
              selectedService={selectedService}
              translit={translit}
              currentSession={currentSession}
              autoOptimizePrompt={autoOptimizePrompt}
            />
          ) : (
            <AssistantMessage
              content={message.content}
              index={index}
              engine={message.engine}
              model={message.model}
              service={message.service}
              systemPromptName={message.systemPromptName}
              fontSize={fontSize}
              fontFamily={fontFamily}
              fontColor={projectFontColor}
              selectedEngine={selectedEngine}
              selectedService={selectedService}
              usedProjectContext={message.usedProjectContext || false}
              projectName={currentProject?.name}
              chainOfThought={message.chainOfThought}
              autoOptimizePrompt={autoOptimizePrompt} 
              enableThinking={enableThinking} />
          )}
        </div>
      ))}
    </div>
  );
};

export default ChatMessages;